export type ExternalMonitoringPeriodBounds = { begin: number; end: number };

export type ExternalMonitoringGroupingInterval = 'day' | 'week' | 'month';

/** Минимум календарных дней в диапазоне для группировки «неделя». */
export const EXTERNAL_MONITORING_MIN_DAYS_FOR_WEEK = 21;

/** Минимум календарных дней в диапазоне для группировки «месяц». */
export const EXTERNAL_MONITORING_MIN_DAYS_FOR_MONTH = 90;

/** Минимальный период (месяцев), если группировка по месяцу — иначе один bucket на весь диапазон. */
export const EXTERNAL_MONITORING_MONTH_GROUPING_MIN_PERIOD_KEY = '3';

/**
 * При интервале группировки «месяц» период «1 месяц» недопустим для external-reviews и старых сценариев.
 */
export function normalizeExternalMonitoringPeriodKey(
  periodKey: string,
  groupingInterval: ExternalMonitoringGroupingInterval,
): string {
  if (groupingInterval === 'month' && periodKey === '1') {
    return EXTERNAL_MONITORING_MONTH_GROUPING_MIN_PERIOD_KEY;
  }
  return periodKey;
}

/** Опции периода для страницы external-reviews (id = число месяцев назад). */
export const EXTERNAL_MONITORING_PERIOD_OPTIONS = [
  { id: '1', value: '1 месяц' },
  { id: '3', value: '3 месяца' },
  { id: '6', value: '6 месяцев' },
  { id: '9', value: '9 месяцев' },
  { id: '12', value: '12 месяцев' },
] as const;

export function buildExternalMonitoringPeriod(months: string): {
  begin: number;
  end: number;
  labelRu: string;
} {
  const m = Math.max(1, Number(months) || 1);
  const end = Math.floor(Date.now() / 1000);
  const from = new Date();
  from.setMonth(from.getMonth() - m);
  const begin = Math.floor(from.getTime() / 1000);
  const labelRu = formatExternalMonitoringPeriodLabelRu(begin, end);
  return { begin, end, labelRu };
}

/** Подпись периода для UI (локальные даты по границам unix). */
export function formatExternalMonitoringPeriodLabelRu(beginSec: number, endSec: number): string {
  const fmt = (ts: number) =>
    new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(ts * 1000));
  return `${fmt(beginSec)} — ${fmt(endSec)}`;
}

/**
 * Число календарных дней в диапазоне [beginSec, endSec] включительно (локальная дата начала и конца).
 */
export function externalMonitoringRangeDayCountInclusive(beginSec: number, endSec: number): number {
  const s = new Date(beginSec * 1000);
  const e = new Date(endSec * 1000);
  const d0 = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const d1 = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  const diffMs = d1.getTime() - d0.getTime();
  const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
  return Math.max(1, days + 1);
}

export function clampExternalMonitoringGroupingInterval(
  rangeDays: number,
  interval: ExternalMonitoringGroupingInterval,
): ExternalMonitoringGroupingInterval {
  let g = interval;
  if (rangeDays < EXTERNAL_MONITORING_MIN_DAYS_FOR_MONTH && g === 'month') {
    g = rangeDays >= EXTERNAL_MONITORING_MIN_DAYS_FOR_WEEK ? 'week' : 'day';
  }
  if (rangeDays < EXTERNAL_MONITORING_MIN_DAYS_FOR_WEEK && g === 'week') {
    g = 'day';
  }
  return g;
}

/** Начало локального календарного дня → unix sec. */
export function localDayStartToUnixSec(d: Date): number {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.floor(x.getTime() / 1000);
}

/** Конец локального календарного дня → unix sec. */
export function localDayEndToUnixSec(d: Date): number {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return Math.floor(x.getTime() / 1000);
}

/**
 * Диапазон из двух дат в календаре: с полуночи «с» по конец дня «по», не позже now.
 * Если «с» > «по», даты меняются местами.
 */
export function boundsFromLocalDateRange(
  from: Date,
  to: Date,
  now: Date = new Date(),
): { begin: number; end: number; labelRu: string } {
  const todayStart = localDayStartToUnixSec(now);
  let d0 = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let d1 = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  if (d0 > d1) {
    const t = d0;
    d0 = d1;
    d1 = t;
  }
  let begin = Math.floor(d0.getTime() / 1000);
  if (begin > todayStart) {
    begin = todayStart;
  }
  const nowSec = Math.floor(now.getTime() / 1000);
  const endOfTo = localDayEndToUnixSec(d1);
  let end = Math.min(endOfTo, nowSec);
  if (end < begin) {
    end = nowSec;
  }
  const labelRu = formatExternalMonitoringPeriodLabelRu(begin, end);
  return { begin, end, labelRu };
}

/** Пресет «N месяцев назад — сейчас», как у старого селекта дашборда. */
export function buildExternalMonitoringDashboardPresetMonths(months: number): {
  begin: number;
  end: number;
  labelRu: string;
} {
  const m = Math.max(1, Math.min(120, Math.floor(months) || 1));
  return buildExternalMonitoringPeriod(String(m));
}

/** Допуск: конец «как сейчас» при расчёте пресета; начало — из‑за разной длины месяца. */
const PRESET_ACTIVE_END_SLACK_SEC = 900;
const PRESET_ACTIVE_BEGIN_SLACK_SEC = 86_400;

/**
 * Совпадает ли текущий диапазон с пресетом «N месяцев» (пересчитанным на текущий момент).
 * Ручной выбор дат обычно не совпадёт; устаревший end (не «сейчас») — пресет не подсвечивается.
 */
export function isExternalMonitoringDashboardPresetActive(
  beginSec: number,
  endSec: number,
  months: number,
): boolean {
  const { begin: eb, end: ee } = buildExternalMonitoringDashboardPresetMonths(months);
  if (Math.abs(endSec - ee) > PRESET_ACTIVE_END_SLACK_SEC) return false;
  return Math.abs(beginSec - eb) <= PRESET_ACTIVE_BEGIN_SLACK_SEC;
}

/** После смены дат или интервала — поджать grouping под длину диапазона. */
export function withClampedExternalMonitoringGrouping<
  T extends {
    periodBeginSec: number;
    periodEndSec: number;
    groupingInterval: ExternalMonitoringGroupingInterval;
  },
>(state: T): T {
  const days = externalMonitoringRangeDayCountInclusive(state.periodBeginSec, state.periodEndSec);
  return {
    ...state,
    groupingInterval: clampExternalMonitoringGroupingInterval(days, state.groupingInterval),
  };
}
